import { TimetablePrintColorPositions, TimetablePrintLayoutOptions } from '@bp/planung-graphql-types';
import { Grid, GridColumn, GridRow, SelectOptionType } from '@bp/ui-components';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  TimetablePrintConfigurationFormType,
  TimetablePrintLayoutPositions,
} from '../forms/TimetablePrintConfigurationForm';
import styles from './TimetablePrintConfigurationGrid.module.scss';
import { TimetablePrintConfigurationGridTile } from './TimetablePrintConfigurationGridTile';

export const TimetablePrintConfigurationGrid = () => {
  const { t } = useTranslation();

  const { values } = useFormikContext<TimetablePrintConfigurationFormType>();
  const { colorPosition, color } = values;

  const white = '#f7f7f7';

  const background =
    colorPosition === TimetablePrintColorPositions.Top
      ? `linear-gradient(0deg, ${white} 90%, ${color} 10%)`
      : colorPosition === TimetablePrintColorPositions.Right
        ? `linear-gradient(90deg, ${white} 95%, ${color} 5%)`
        : colorPosition === TimetablePrintColorPositions.Bottom
          ? `linear-gradient(180deg, ${white} 90%, ${color} 10%)`
          : colorPosition === TimetablePrintColorPositions.Left
            ? `linear-gradient(270deg, ${white} 95%, ${color} 5%)`
            : colorPosition === TimetablePrintColorPositions.None
              ? white
              : color;

  const configOptions: SelectOptionType[] = [
    {
      label: t('timetable.configuration.options.subjectShort'),
      value: TimetablePrintLayoutOptions.SubjectShort,
    },
    {
      label: t('timetable.configuration.options.subjectLong'),
      value: TimetablePrintLayoutOptions.SubjectLong,
    },
    {
      label: t('classGroups.group'),
      value: TimetablePrintLayoutOptions.GroupName,
    },
    {
      label: t('classes.title', { count: 1 }),
      value: TimetablePrintLayoutOptions.ClassName,
    },
    {
      label: `${t('classes.title', { count: 1 })}/${t('classGroups.group')}`,
      value: TimetablePrintLayoutOptions.ClassAndGroup,
    },
    {
      label: t('timetable.configuration.options.teacherShort'),
      value: TimetablePrintLayoutOptions.TeacherShort,
    },
    {
      label: t('timetable.configuration.options.teacherLong'),
      value: TimetablePrintLayoutOptions.TeacherLong,
    },
    {
      label: t('rooms.title', { count: 1 }),
      value: TimetablePrintLayoutOptions.RoomName,
    },
    {
      label: t('rooms.roomNumber'),
      value: TimetablePrintLayoutOptions.RoomNumber,
    },
    {
      label: `${t('timetable.configuration.options.teacher')}/${t('rooms.title', { count: 1 })}`,
      value: TimetablePrintLayoutOptions.TeacherAndRoom,
    },
    {
      label: `${t('timetable.configuration.options.subjectShort')} ${t('classes.title', { count: 1 })}/${t('classGroups.group')}`,
      value: TimetablePrintLayoutOptions.SubjectClassAndGroup,
    },
  ];

  const getPreview = (option: TimetablePrintLayoutOptions) => {
    let preview = '';
    switch (option) {
      case TimetablePrintLayoutOptions.SubjectShort:
        preview = t('timetable.configuration.preview.subjectShort');
        break;
      case TimetablePrintLayoutOptions.SubjectLong:
        preview = t('timetable.configuration.preview.subjectLong');
        break;
      case TimetablePrintLayoutOptions.GroupName:
        preview = t('timetable.configuration.preview.groupName');
        break;
      case TimetablePrintLayoutOptions.ClassName:
        preview = t('timetable.configuration.preview.className');
        break;
      case TimetablePrintLayoutOptions.ClassAndGroup:
        preview = t('timetable.configuration.preview.classAndGroup');
        break;
      case TimetablePrintLayoutOptions.TeacherShort:
        preview = t('timetable.configuration.preview.teacherShort');
        break;
      case TimetablePrintLayoutOptions.TeacherLong:
        preview = t('timetable.configuration.preview.teacherLong');
        break;
      case TimetablePrintLayoutOptions.RoomName:
        preview = t('timetable.configuration.preview.roomName');
        break;
      case TimetablePrintLayoutOptions.RoomNumber:
        preview = t('timetable.configuration.preview.roomNumber');
        break;
      case TimetablePrintLayoutOptions.TeacherAndRoom:
        preview = t('timetable.configuration.preview.teacherAndRoom');
        break;
      case TimetablePrintLayoutOptions.SubjectClassAndGroup:
        preview = t('timetable.configuration.preview.subjectClassAndGroup');
        break;
    }
    return preview;
  };

  return (
    <Grid className={styles['timetable-print-configuration-grid']} useFormGap>
      <GridRow>
        <GridColumn width={9}>
          <div className={styles.config}>
            <div className={styles.top}>
              <TimetablePrintConfigurationGridTile
                position={TimetablePrintLayoutPositions.topLeft}
                value={values[TimetablePrintLayoutPositions.topLeft]}
                options={configOptions}
              />
              <TimetablePrintConfigurationGridTile
                position={TimetablePrintLayoutPositions.topCenter}
                value={values[TimetablePrintLayoutPositions.topCenter]}
                options={configOptions}
                disabled={true}
              />
              <TimetablePrintConfigurationGridTile
                position={TimetablePrintLayoutPositions.topRight}
                value={values[TimetablePrintLayoutPositions.topRight]}
                options={configOptions}
              />
            </div>
            <div className={styles.center}>
              <TimetablePrintConfigurationGridTile
                isMandatory
                position={TimetablePrintLayoutPositions.center}
                value={values[TimetablePrintLayoutPositions.center]}
                options={configOptions}
              />
            </div>
            <div className={styles.bottom}>
              <TimetablePrintConfigurationGridTile
                position={TimetablePrintLayoutPositions.bottomLeft}
                value={values[TimetablePrintLayoutPositions.bottomLeft]}
                options={configOptions}
              />
              <TimetablePrintConfigurationGridTile
                position={TimetablePrintLayoutPositions.bottomCenter}
                value={values[TimetablePrintLayoutPositions.bottomCenter]}
                options={configOptions}
                disabled={true}
              />
              <TimetablePrintConfigurationGridTile
                position={TimetablePrintLayoutPositions.bottomRight}
                value={values[TimetablePrintLayoutPositions.bottomRight]}
                options={configOptions}
              />
            </div>
          </div>
        </GridColumn>

        <GridColumn width={3}>
          <div
            className={styles.preview}
            style={{ background: colorPosition === TimetablePrintColorPositions.Full ? color : background }}
          >
            <div className={styles.top}>
              <div>{getPreview(values[TimetablePrintLayoutPositions.topLeft])}</div>
              <div>{getPreview(values[TimetablePrintLayoutPositions.topCenter])}</div>
              <div>{getPreview(values[TimetablePrintLayoutPositions.topRight])}</div>
            </div>
            <div className={styles.center}>
              <div>{getPreview(values[TimetablePrintLayoutPositions.center])}</div>
            </div>
            <div className={styles.bottom}>
              <div>{getPreview(values[TimetablePrintLayoutPositions.bottomLeft])}</div>
              <div>{getPreview(values[TimetablePrintLayoutPositions.bottomCenter])}</div>
              <div>{getPreview(values[TimetablePrintLayoutPositions.bottomRight])}</div>
            </div>
          </div>
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
